// localStorage.ts - Service for managing wheels in localStorage
import { v4 as uuidv4 } from 'uuid';

import { Atm, CreateAtmDto } from '../types/atm';
import { Wheel, WheelSettings, WheelTheme } from '../types/wheel';

const WHEEL_PREFIX = 'spindom-wheel-';
const ATM_PREFIX = 'spindom-atm-';

/**
 * Interface for wheels stored in localStorage with additional metadata
 */
export interface StoredWheel extends Wheel {
  // Additional fields for local wheels
  isLocal: true;
  lastModified: string; // ISO date string
}

/**
 * Get all stored wheels from localStorage
 */
export const getStoredWheels = (): StoredWheel[] => {
  try {
    const wheels: StoredWheel[] = [];

    // Loop through localStorage to find all stored wheels
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(WHEEL_PREFIX)) {
        const wheelData = localStorage.getItem(key);
        if (wheelData) {
          const wheel = JSON.parse(wheelData) as StoredWheel;
          wheels.push(wheel);
        }
      }
    }

    // Sort by last modified (most recent first)
    return wheels.sort((a, b) => {
      return new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime();
    });
  } catch (error) {
    console.error('Error retrieving wheels from localStorage:', error);
    return [];
  }
};

/**
 * Get a specific wheel by ID from localStorage
 */
export const getStoredWheelById = (id: string): StoredWheel | null => {
  try {
    const wheelData = localStorage.getItem(`${WHEEL_PREFIX}${id}`);
    if (!wheelData) return null;
    return JSON.parse(wheelData) as StoredWheel;
  } catch (error) {
    console.error(`Error retrieving wheel ${id} from localStorage:`, error);
    return null;
  }
};

/**
 * Save a new wheel to localStorage
 */
export const saveWheel = (wheelData: Partial<Wheel>): StoredWheel => {
  try {
    const id = uuidv4();
    const now = new Date();

    // Ensure all required properties have values
    const storedWheel: StoredWheel = {
      id,
      name: wheelData.name || 'Untitled Wheel',
      entries: wheelData.entries || [],
      theme: wheelData.theme || WheelTheme.DEFAULT,
      createdAt: now,
      updatedAt: now,
      createdBy: null, // No user ID for local storage
      isPublic: false,
      spinCount: 0, // New wheels start with 0 spins
      favorites: 0,
      wheelSettings: wheelData.wheelSettings || {
        allowDuplicateSpins: true,
        wheelStyle: 'default',
      },
      // Additional local storage properties
      isLocal: true,
      lastModified: now.toISOString(),
      // Optional properties
      description: wheelData.description,
      custom_colors: wheelData.custom_colors,
      category: wheelData.category,
      difficulty: wheelData.difficulty,
      tags: wheelData.tags,
    };

    localStorage.setItem(`${WHEEL_PREFIX}${id}`, JSON.stringify(storedWheel));
    return storedWheel;
  } catch (error) {
    console.error('Error saving wheel to localStorage:', error);
    throw new Error('Failed to save wheel');
  }
};

/**
 * Update an existing wheel in localStorage
 */
export const updateStoredWheel = (wheel: StoredWheel): StoredWheel => {
  try {
    // Update the last modified timestamp
    const updatedWheel: StoredWheel = {
      ...wheel,
      lastModified: new Date().toISOString(),
    };

    localStorage.setItem(`${WHEEL_PREFIX}${wheel.id}`, JSON.stringify(updatedWheel));
    return updatedWheel;
  } catch (error) {
    console.error(`Error updating wheel ${wheel.id} in localStorage:`, error);
    throw new Error('Failed to update wheel');
  }
};

/**
 * Delete a wheel from localStorage
 */
export const deleteStoredWheel = (id: string): boolean => {
  try {
    localStorage.removeItem(`${WHEEL_PREFIX}${id}`);
    return true;
  } catch (error) {
    console.error(`Error deleting wheel ${id} from localStorage:`, error);
    return false;
  }
};

/**
 * Increment the spin count for a wheel
 */
export const incrementSpinCount = (id: string): StoredWheel | null => {
  try {
    const wheel = getStoredWheelById(id);
    if (!wheel) return null;

    const updatedWheel: StoredWheel = {
      ...wheel,
      spinCount: wheel.spinCount + 1,
      updatedAt: new Date(),
      lastModified: new Date().toISOString(),
    };

    localStorage.setItem(`${WHEEL_PREFIX}${id}`, JSON.stringify(updatedWheel));
    return updatedWheel;
  } catch (error) {
    console.error(`Error incrementing spin count for wheel ${id}:`, error);
    return null;
  }
};

/**
 * Clear all stored wheels (utility function)
 */
export const clearStoredWheels = (): boolean => {
  try {
    // Only remove items that match our wheel prefix
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(WHEEL_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
    return true;
  } catch (error) {
    console.error('Error clearing wheels from localStorage:', error);
    return false;
  }
};

// ==============================================
// ATM STORAGE FUNCTIONS
// ==============================================

/**
 * Interface for ATMs stored in localStorage with additional metadata
 */
export interface StoredAtm extends Atm {
  // Additional fields for local ATMs
  isLocal: true;
  lastModified: string; // ISO date string
}

/**
 * Get all stored ATMs from localStorage
 */
export const getStoredAtms = (): StoredAtm[] => {
  try {
    const atms: StoredAtm[] = [];

    console.log('🔍 LocalStorage Debug: Starting getStoredAtms()');
    console.log('🔍 LocalStorage Debug: localStorage.length =', localStorage.length);
    console.log('🔍 LocalStorage Debug: ATM_PREFIX =', ATM_PREFIX);

    // Debug: Show all keys in localStorage
    const allKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      allKeys.push(key);
    }
    console.log('🔍 LocalStorage Debug: All keys in localStorage:', allKeys);

    // MIGRATION: Check for old storage format
    const oldAtmData = localStorage.getItem('spoil_engine_atms');
    if (oldAtmData) {
      console.log(
        '🔄 LocalStorage Migration: Found old ATM data:',
        oldAtmData.substring(0, 200) + '...'
      );
      try {
        const oldAtms = JSON.parse(oldAtmData);
        console.log('🔄 LocalStorage Migration: Parsed old ATMs:', oldAtms.length);

        // Migrate old ATMs to new format
        if (Array.isArray(oldAtms) && oldAtms.length > 0) {
          console.log('🔄 LocalStorage Migration: Migrating ATMs to new format...');
          oldAtms.forEach((oldAtm: any, index: number) => {
            const now = new Date();
            const newAtm: StoredAtm = {
              ...oldAtm,
              // Ensure dates are never null - dates will be serialized to strings in localStorage automatically
              createdAt: oldAtm.createdAt ? new Date(oldAtm.createdAt) : now,
              updatedAt: oldAtm.updatedAt ? new Date(oldAtm.updatedAt) : now,
              // Ensure these required fields exist
              transactionCount: oldAtm.transactionCount || 0,
              transactions: oldAtm.transactions || [],
              isLocal: true,
              lastModified: now.toISOString(),
            } as StoredAtm;
            localStorage.setItem(`${ATM_PREFIX}${oldAtm.id}`, JSON.stringify(newAtm));
            console.log(
              `🔄 LocalStorage Migration: Migrated ATM ${index + 1}/${oldAtms.length}: ${oldAtm.id}`
            );
          });

          // Remove old data after successful migration
          localStorage.removeItem('spoil_engine_atms');
          console.log('🔄 LocalStorage Migration: Migration complete, old data removed');
        }
      } catch (migrationError) {
        console.error('🔄 LocalStorage Migration: Error during migration:', migrationError);
      }
    }

    // Loop through localStorage to find all stored ATMs
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      console.log('🔍 LocalStorage Debug: Checking key:', key);

      if (key && key.startsWith(ATM_PREFIX)) {
        console.log('🔍 LocalStorage Debug: Key matches ATM prefix:', key);
        const atmData = localStorage.getItem(key);
        console.log(
          '🔍 LocalStorage Debug: Raw atmData:',
          atmData ? atmData.substring(0, 100) + '...' : 'null'
        );

        if (atmData) {
          try {
            const atm = JSON.parse(atmData) as StoredAtm;
            console.log('🔍 LocalStorage Debug: Parsed ATM:', atm.id, atm.name);
            atms.push(atm);
          } catch (parseError) {
            console.error('🔍 LocalStorage Debug: Error parsing ATM data for key', key, parseError);
          }
        }
      } else if (key) {
        console.log('🔍 LocalStorage Debug: Key does NOT match ATM prefix:', key);
      }
    }

    console.log('🔍 LocalStorage Debug: Final ATMs array length:', atms.length);
    console.log(
      '🔍 LocalStorage Debug: Final ATMs:',
      atms.map(a => ({ id: a.id, name: a.name }))
    );

    // Sort by last modified (most recent first)
    return atms.sort((a, b) => {
      return new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime();
    });
  } catch (error) {
    console.error('Error retrieving ATMs from localStorage:', error);
    return [];
  }
};

/**
 * Get a specific ATM by ID from localStorage
 */
export const getStoredAtmById = (id: string): StoredAtm | null => {
  try {
    const atmData = localStorage.getItem(`${ATM_PREFIX}${id}`);
    if (!atmData) return null;
    return JSON.parse(atmData) as StoredAtm;
  } catch (error) {
    console.error(`Error retrieving ATM ${id} from localStorage:`, error);
    return null;
  }
};

/**
 * Save an ATM to localStorage
 */
export const saveAtm = async (atmData: CreateAtmDto): Promise<StoredAtm> => {
  try {
    const id = uuidv4();
    const now = new Date();
    const isoNow = now.toISOString();

    const atm: StoredAtm = {
      id,
      name: atmData.name,
      description: atmData.description,
      options: atmData.options,
      theme: atmData.theme,
      isPublic: atmData.isPublic,
      atmSettings: atmData.atmSettings,
      createdBy: 'anonymous',
      createdByName: 'Anonymous User',
      createdAt: now,
      updatedAt: now,
      transactionCount: 0,
      transactions: [],
      isLocal: true,
      lastModified: isoNow,
    };

    localStorage.setItem(`${ATM_PREFIX}${id}`, JSON.stringify(atm));
    return atm;
  } catch (error) {
    console.error('Error saving ATM to localStorage:', error);
    throw new Error('Failed to save ATM to localStorage');
  }
};

/**
 * Update an ATM in localStorage
 */
export const updateStoredAtm = (id: string, updateData: Partial<Atm>): StoredAtm | null => {
  try {
    const existingAtm = getStoredAtmById(id);
    if (!existingAtm) return null;

    const updatedAtm: StoredAtm = {
      ...existingAtm,
      ...updateData,
      updatedAt: new Date(),
      lastModified: new Date().toISOString(),
    };

    localStorage.setItem(`${ATM_PREFIX}${id}`, JSON.stringify(updatedAtm));
    return updatedAtm;
  } catch (error) {
    console.error(`Error updating ATM ${id} in localStorage:`, error);
    return null;
  }
};

/**
 * Delete an ATM from localStorage
 */
export const deleteStoredAtm = (id: string): boolean => {
  try {
    localStorage.removeItem(`${ATM_PREFIX}${id}`);
    return true;
  } catch (error) {
    console.error(`Error deleting ATM ${id} from localStorage:`, error);
    return false;
  }
};

/**
 * Clear all stored ATMs (utility function)
 */
export const clearStoredAtms = (): boolean => {
  try {
    // Only remove items that match our ATM prefix
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(ATM_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
    return true;
  } catch (error) {
    console.error('Error clearing ATMs from localStorage:', error);
    return false;
  }
};
